import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareProblems = (p1, p2) => p1.internalid === p2.internalid; 
const transformProblem = p => {
    if (p.simple !== undefined) {
        return {...p, simple: p.simple ? 1 : 0};
    }
    return p
}

const problemSlice = createSlice({
    name: 'problems',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload.map(p => ({...p, simple: p.simple === '1'}))
        },
        addOrUpdate: (state, action) => {
            for (const p of ensureArray(action.payload)) {
                p.simple = p.simple === '1';
                let idx = state.data.findIndex(p2 => compareProblems(p, p2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, p);
                } else {
                    state.data.push(p);
                }
            }
        },
        remove: (state, action) => {
            for (const p of ensureArray(action.payload)) {
                let idx = state.data.findIndex(p2 => compareProblems(p2, p));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => problemSlice.getInitialState());
    }
});

// #region network operations
const fetchProblems = network => network.post('/api/v1/problems', {}, problemSlice.actions.set);
export const createProblem = (network, data) => network.post('/api/v1/problem', transformProblem(data), problemSlice.actions.addOrUpdate);
export const updateProblem = (network, data) => network.put(`/api/v1/problem`, transformProblem(data), problemSlice.actions.addOrUpdate);
export const removeProblem = (network, data) => network.delete(`/api/v1/problem/${encodeURIComponent(data.internalid)}`, problemSlice.actions.remove);
// #endregion

// #region selectors
const selectProblems = state => state.problems.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectProblemBySiteLevelAndType = createSelector([selectProblems, (problems, site) => site, (problems, site, level) => level, (problems, site, level, type) => type], (problems, site, level, type) => {
    return problems.filter(p => (site === undefined || p.site === null || p.site === site) && (level === undefined || p.level === null || p.level === level) && (type === undefined || p.type === null || p.type === type))
});
const selectProblemForSiteLevelType = (site, level, type) => state => selectProblemBySiteLevelAndType(state, site, level, type);

const selectProblemByName = createSelector([selectProblems, (problems, site) => site, (probelms, site, level) => level, (problems, site, level, type) => type, (problems, site, level, type, name) => name], (problems, site, level, type, name) => {
    let result = problems.find(p => (!site || p.site === site) && (!level || p.level === level) && (!type || p.type === type) && p.problem === name);
    if (!result) {
        result = problems.find(p => (!site || p.site === site) && (!level || p.level === level) && !p.type && p.problem === name);
    }
    if (!result) {
        result = problems.find(p => (!site || p.site === site) && !p.level && (!type || p.type === type) && p.problem === name);
    }
    if (!result) {
        result = problems.find(p => !p.site && (!level || p.level === level) && (!type || p.type === type) && p.problem === name);
    }
    if (!result) {
        result = problems.find(p => (!site || p.site === site) && !p.level && !p.type && p.problem === name);
    }
    if (!result) {
        result = problems.find(p => !p.site && (!level || p.level === level) && !p.type && p.problem === name);
    }
    if (!result) {
        result = problems.find(p => !p.site && !p.level && (!type || p.type === type) && p.problem === name);
    }
    if (!result) {
        result = problems.find(p => !p.site && !p.level && !p.type && p.problem === name);
    }
    return result;
})
const selectProblem = (site, level, type, name) => state => selectProblemByName(state, site, level, type, name);
// #endregion

// #region listeners
const listen = {
    problemCreated: (problem, { dispatch }) => dispatch(problemSlice.actions.addOrUpdate(problem)),
    problemUpdated: (problem, { dispatch }) => dispatch(problemSlice.actions.addOrUpdate(problem)),
    problemRemoved: (problem, { dispatch }) => dispatch(problemSlice.actions.remove(problem))
}
// #endregion

// #region permission checks
export const canUpdateProblem = (credentials, problem) => credentials.access.hasFull('administrator');
export const canCreateProblem = (credentials, problem) => credentials.access.hasFull('administrator');
export const canRemoveProblem = (credentials, problem) => credentials.access.hasFull('administrator');
// #endregion

export const [useProblems] = dataHookFactory(selectProblems, { run: fetchProblems, listen });
export const [useProblemsForSiteLevelType] = dataHookFactory(selectProblemForSiteLevelType, (site, level, type) => ({ run: fetchProblems, listen }));
export const [useProblem,realizeProblem] = dataHookFactory(selectProblem, (site, level, type, name) => ({ run: fetchProblems, listen }));

export const problemInit = { run: fetchProblems, listen }

const reducerDetails = { name: problemSlice.name, reducer: problemSlice.reducer };
export default reducerDetails;